import React, { useState } from 'react';
import { Button, Typography, Card, IconButton, Grid, CardMedia, CardHeader, Box } from '@mui/material';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import { toast, ToastContainer } from 'react-toastify';
import { uploadImages } from '../../asiosConfig'; // Adjust path if needed
import { uploadImageSchema } from '../ValidationSchema';
import lightIcon from "../../images/lightIcon.png";
import blurPic from "../../images/blurPic.png";
import sidePic from "../../images/sidePic.png";
import watermarkPic from "../../images/watermarkPic.png";
import groupPic from "../../images/groupPic.png";
import { DisabledByDefaultTwoTone } from '@mui/icons-material';

const ImageUpload = () => {
    const [images, setImages] = useState([]);
    const [fileList, setFileList] = useState([]);
    const navigate = useNavigate();
    const token = localStorage.getItem('token');
    const resizeImage = (file, maxWidth = 800, maxHeight = 800) => {
        return new Promise((resolve) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = (event) => {
                const img = new Image();
                img.src = event.target.result;
                img.onload = () => {
                    const canvas = document.createElement("canvas");
                    let width = img.width;
                    let height = img.height;
    
                    if (width > maxWidth || height > maxHeight) {
                        if (width > height) {
                            height = Math.round((maxWidth / width) * height);
                            width = maxWidth;
                        } else {
                            width = Math.round((maxHeight / height) * width);
                            height = maxHeight;
                        }
                    }
    
                    canvas.width = width;
                    canvas.height = height;
                    const ctx = canvas.getContext("2d");
                    ctx.drawImage(img, 0, 0, width, height);
    
                    canvas.toBlob((blob) => {
                        resolve(new File([blob], file.name, { type: "image/jpeg", lastModified: Date.now() }));
                    }, "image/jpeg", 0.7); // 70% quality
                };
            };
        });
    };
    
    const handleImageChange = async (e) => {
        const files = Array.from(e.target.files);
        const compressedFiles = await Promise.all(files.map(file => resizeImage(file)));
    
        const imageUrls = compressedFiles.map(file => URL.createObjectURL(file));
    
        setImages(prevImages => [...prevImages, ...imageUrls]);
        setFileList(prevFiles => [...prevFiles, ...compressedFiles]);
    };
    

    const removeImage = (index) => {
        setImages(prevImages => prevImages.filter((_, i) => i !== index));
        setFileList(prevFiles => prevFiles.filter((_, i) => i !== index));
    };

    // const token = localStorage.getItem('token');

    const formik = useFormik({
        initialValues: {
            photos: [],
            token: token
        },
        validationSchema: uploadImageSchema,
        onSubmit: async (values, { setSubmitting }) => {
            try {
                const formData = new FormData();
                fileList.forEach(file => formData.append('photos[]', file));
                formData.append('token', values.token);

                const response = await uploadImages(formData);
                if (response.status === 200 || response.status === 201) {
                    toast.success('Successfully uploaded the photos');
                    formik.resetForm();
                    setImages([]);
                    setFileList([]);
                } else {
                    toast.error(response.data.message || 'Failed to upload photos');
                }
            } catch (error) {
                toast.error('Failed to upload photos: ' + error.message);
            } finally {
                setSubmitting(false);
            }
        },
    });

    return (
        <div className="col-lg-9 col-12">
            <CardHeader
                avatar={
                    <ArrowBackIcon sx={{ color: '#FF8008' }} onClick={() => navigate(-1)} />
                }
                title="Add Photos"
                titleTypographyProps={{ sx: { color: '#FF8008', fontSize: '20px', fontWeight: '500' } }}
            />
            <div className="blackTxt text-center mt-3">
                <p className="m-0">Impress them with your latest photo</p>
                <p className="matchtxt">Profile photos ensure a 5% increase in matches</p>
            </div>
            <form onSubmit={formik.handleSubmit}>
                <div className="profileDetailsBlock py-2">
                    <div className="text-center">
                        <label htmlFor="upload-photo">
                            <input
                                id="upload-photo"
                                name="photos"
                                type="file"
                                multiple
                                accept="image/*"
                                style={{ display: 'none' }}
                                onChange={handleImageChange}
                            />
                            <Button
                                variant="contained"
                                component="span"
                                startIcon={<PhotoCamera />}
                                sx={{ backgroundColor: '#FF8008', '&:hover': { backgroundColor: 'orange' } }}
                            >
                                Upload Photo
                            </Button>
                        </label>
                        <Typography variant="body2" align="center" className="matchtxt mt-1">
                            Supported file format jpg, jpeg, png, gif | up to 2 MB
                        </Typography>
                    </div>
                    <Grid container spacing={2} justifyContent="center" mt={2}>
                        {images.map((image, index) => (
                            <Grid item xs={4} sm={3} md={2} key={index}>
                                <Card style={{ position: 'relative' }}>
                                    <CardMedia
                                        component="img"
                                        alt={`Preview ${index}`}
                                        height="140"
                                        image={image}
                                        title={`Preview ${index}`}
                                    />
                                    <IconButton
                                        color="error"
                                        onClick={() => removeImage(index)}
                                        style={{ position: 'absolute', top: 8, right: 8 }}
                                    >
                                        <DisabledByDefaultTwoTone />
                                    </IconButton>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                    {images.length > 0 && (  // Only show the button if there are images
                        <div className="d-grid col-4 mx-auto mt-2">
                            <Button type="submit" variant="contained" sx={{ backgroundColor: '#FF8008', '&:hover': { backgroundColor: 'orange' } }}>
                                {formik.isSubmitting ? 'Saving...' : 'Save Changes'}
                            </Button>
                        </div>
                    )}
                    <hr />
                    <div className="text-center mt-5">
                        <Typography variant="h6" className="tips" display="flex" alignItems="center" justifyContent="center">
                            <img src={lightIcon} alt="Tips Icon" style={{ marginRight: '8px' }} />
                            Here are a few tips
                        </Typography>
                        <Typography variant="body1" className="avoidTxt">
                            Avoid the following photos to highlight your profile better
                        </Typography>

                        <Grid container spacing={4} justifyContent="center" py={4}>
                            <Grid item xs={6} sm={3}>
                                <Box display="flex" flexDirection="column" alignItems="center" textAlign="center">
                                    <img src={blurPic} alt="Blur Photos" style={{ maxWidth: '100%', height: 'auto' }} />
                                    <Typography variant="body2" className="photosTxtl" mt={2}>
                                        Blur Photos
                                    </Typography>
                                </Box>
                            </Grid>

                            <Grid item xs={6} sm={3}>
                                <Box display="flex" flexDirection="column" alignItems="center" textAlign="center">
                                    <img src={sidePic} alt="Side Face" style={{ maxWidth: '100%', height: 'auto' }} />
                                    <Typography variant="body2" className="photosTxtl" mt={2}>
                                        Side Face
                                    </Typography>
                                </Box>
                            </Grid>

                            <Grid item xs={6} sm={3}>
                                <Box display="flex" flexDirection="column" alignItems="center" textAlign="center">
                                    <img src={watermarkPic} alt="Watermark" style={{ maxWidth: '100%', height: 'auto' }} />
                                    <Typography variant="body2" className="photosTxtl" mt={2}>
                                        Watermark
                                    </Typography>
                                </Box>
                            </Grid>

                            <Grid item xs={6} sm={3}>
                                <Box display="flex" flexDirection="column" alignItems="center" textAlign="center">
                                    <img src={groupPic} alt="Group Photos" style={{ maxWidth: '100%', height: 'auto' }} />
                                    <Typography variant="body2" className="photosTxtl" mt={2}>
                                        Group Photos
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>
                    </div>
                </div>
            </form>
            <ToastContainer />
        </div>
    );
};

export default ImageUpload;
